import 'isomorphic-fetch';
import getConfig from 'next/config';

import { queryString } from 'shared/util/query';
import { Filters } from '@/interfaces';

const {
    publicRuntimeConfig: { contextPath, apiExternalUrl },
    serverRuntimeConfig: { apiBaseUrl, profileReadUrl },
} = getConfig();

const nodeProxyApiUrl = `${contextPath}/api`;
const requestOptions: RequestInit = { credentials: 'include' };

const authHeader = (token, other = {}) => ({ headers: { authorization: `Bearer ${token ?? ''}`, ...other } });
const createPost = (body): RequestInit => ({
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-requested-with': 'XMLHttpRequest' },
    body: body ? JSON.stringify(body) : '',
    credentials: 'include',
});

const createFormPost = (form): RequestInit => ({
    method: 'POST',
    body: form,
    credentials: 'include',
});

const handleFailure = (response) =>
    response.ok ? Promise.resolve(response.json()) : Promise.reject(`(${response.status}) ${response.statusText}: ${response.url}`);

/* FHH API fetch functions: */

export const fetchResults = (filters: Filters = {}) => {
    const { destination: intentionallyUnused, ...filtersWithoutDestination } = filters;
    return fetch(`${nodeProxyApiUrl}/list/${queryString(filtersWithoutDestination)}`, requestOptions).then(handleFailure);
};

export const fetchMapResults = (filters: Filters = {}) => {
    const { destination: intentionallyUnused, ...filtersWithoutDestination } = filters;
    const url =
        apiBaseUrl !== undefined
            ? `${apiBaseUrl}/map-list/${queryString(filtersWithoutDestination)}`
            : `${nodeProxyApiUrl}/map-list/${queryString(filtersWithoutDestination)}`;
    return fetch(url, requestOptions).then(handleFailure);
};

export const postSaveSearch = (name, notify, filters: Filters = {}, csrfToken, fltToken) => {
    const { destination: intentionallyUnused, ...filtersWithoutDestination } = filters;
    const fhhSavedSearchEntity = JSON.stringify({
        savedSearchName: name,
        mailNotifications: notify,
        webNotifications: false,
        searchParams: queryString(filtersWithoutDestination, false),
    });
    return fetch(`${apiExternalUrl}/save-search/save`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${fltToken}` },
        body: `${fhhSavedSearchEntity}`,
    }).then(handleFailure);
};

export const fetchObject = (adId) => fetch(`${nodeProxyApiUrl}/id/${adId}/`, requestOptions).then(handleFailure);

// TODO move this to another file that is just used server side??
export const fetchObjectSsr = (adId, data) => {
    const url = `${apiBaseUrl}${data?.isPreview ? '/preview' : ''}/id/${adId}/`;

    return fetch(url, authHeader(data?.userToken, { 'x-uniquevisitorid': data?.uniqueVisitorId ?? '' })).then((response) =>
        response.status === 204 || response.status === 404 ? Promise.resolve({ notFound: true }) : handleFailure(response)
    );
};

export const fetchProfileSsr = (loginId) => fetch(`${profileReadUrl}profile/sdrn:finn:user:${loginId}`).then(handleFailure);

export const fetchAutocompleteSuggestions = (filters, sessionToken) =>
    fetch(`${nodeProxyApiUrl}/autocomplete/${queryString(filters)}`, {
        headers: { 'x-gtoken': sessionToken },
    }).then(handleFailure);

export const fetchCalendarAvailability = (data) => fetch(`${apiExternalUrl}/calendar/availability${queryString(data)}`).then(handleFailure);

export const fetchCalendarPrice = (data) => fetch(`${apiExternalUrl}/calendar/price${queryString(data)}`).then(handleFailure);

export const fetchCalendarPriceSsr = (data) => fetch(`${apiBaseUrl}/travel-api/fhh/calendar/price${queryString(data)}`).then(handleFailure);

export const fetchPriceInfoSsr = (adId, userToken) =>
    fetch(`${apiBaseUrl}/travel-api/fhh/price/info/${adId}/`, authHeader(userToken)).then(handleFailure);

export const createOrUpdatePriceInfo = (data) => fetch(`${apiExternalUrl}/price/info/create/`, createPost(data)).then(handleFailure);

export const postPriceOverrideData = (data) => fetch(`${apiExternalUrl}/price/override/create/`, createPost(data)).then(handleFailure);

export const updatePriceOverrideData = (data) => fetch(`${apiExternalUrl}/price/override/update/`, createPost(data)).then(handleFailure);

export const deletePriceOverride = (data) => fetch(`${apiExternalUrl}/price/override/delete/`, createPost(data)).then(handleFailure);

export const fetchObjectPriceInfo = (adId) => fetch(`${apiExternalUrl}/price/summary/${adId}/`).then(handleFailure);

export const updateRentalDurations = (data) => fetch(`${apiExternalUrl}/price/rental/`, createPost(data)).then(handleFailure);

export const postOrderData = (data) => fetch(`${apiExternalUrl}/calendar/order/`, createFormPost(data)).then(handleFailure);

export const updateCalendarState = (action, adId) =>
    fetch(`${apiExternalUrl}/rental/${action}/${adId}/`, createPost(null)).then(handleFailure);

export const fetchBookingData = (adId, userToken) => fetch(`${apiBase()}/booking/list/${adId}/`, authHeader(userToken)).then(handleFailure);

export const postBookingData = (action, data) =>
    fetch(`${apiExternalUrl}/booking/${action}/${data.adId}/`, createPost(data)).then(handleFailure);

export const fetchConversationPartners = (adId) =>
    fetch(`${apiExternalUrl}/user/conversation/${adId}/`, requestOptions).then(handleFailure);

export const fetchObjectRecommendationAds = (adId) =>
    fetch(`${nodeProxyApiUrl}/mlt/travel-fhh/${adId}/?rows=6`, requestOptions).then(handleFailure);

export const fetchPermissionSsr = (adId, userToken) =>
    fetch(`${apiBaseUrl}/travel-api/fhh/price/permission/${adId}/`, authHeader(userToken)).then(handleFailure);

export const trackFusClick = (adId) => fetch(`${apiExternalUrl}/fus/clicked/`, createPost({ adId })).then(handleFailure);

const apiBase = () => {
    if (typeof window === 'undefined') {
        // if (false) {
        return `${apiBaseUrl}/travel-api/fhh`;
    } else {
        return apiExternalUrl;
    }
};
